import React, { useEffect, useState, useRef} from "react";
import axios from "axios";
import {
  Button,
  TextareaAutosize,
  Select,
  MenuItem,
  FormControl,
  CircularProgress,
  Tooltip,
} from "@mui/material";
import { Translate as TranslateIcon } from "@mui/icons-material";
import ClipBoardComponent from "./copy_to_clipboard";
import DownloadButton from "./download_button";
import { LanguagePicker } from "./select_language_button";

export default function Translate() {
  const [inputText, setInputText] = useState(
    "Send Button\nLogin Button\nSending Message"
  );
  const [resultMap, setResultMap] = useState({ text: {} });
  const [inputLanguageKey, setInputLanguageKey] = useState("en");
  const [languageList, setLanguagesList] = useState([]);
  const [selectedLanguages, setSelectedLanguages] = useState(["tr"]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [status, setStatus] = useState("");
  const resultRef = useRef(null);

  const translateText = async () => {
    setLoading(true);
    setError(null);
    try {
      const translations = {};
      console.log("selected language: ", selectedLanguages);

      // Handle the original language separately
      if (!translations[inputLanguageKey]) {
        translations[inputLanguageKey] = {};
      }

      const originalLines = inputText.split("\n");

      originalLines.forEach((originalLine) => {
        const trimmedOriginalLine = originalLine.trim();

        // Skip empty lines
        if (trimmedOriginalLine !== "") {
          translations[inputLanguageKey][trimmedOriginalLine] =
            trimmedOriginalLine;
        }
      });
      setStatus("")

      const totalLanguages = selectedLanguages.length;
      // Translate to other selected languages
      for (let index = 0; index < totalLanguages; index++) {
        const languageCode = selectedLanguages[index].trim();

        // languageCode = languageCode.trim();

        // Skip if input and output languages are the same
        if (languageCode === inputLanguageKey) {
          continue;
        }

        const options = {
          method: "POST",
          url: "https://script.google.com/macros/s/AKfycbyuJ1FaSALIta6fVga9oxoYBHn1d7ee25N-pQgOKQF5OkXEdgNpjuhloLIxZnwgK9fUig/exec",
          headers: {
            "content-type": "application/x-www-form-urlencoded",
          },
          data: {
            source_lang: inputLanguageKey,
            target_lang: languageCode,
            text: inputText,
          },
        };
        setStatus("translating: " + languageCode + "..." + `(${index + 1}/${totalLanguages})`);
        const response = await axios.request(options);

        const translatedText = response.data.translatedText.trim();

        if (!translations[languageCode]) {
          translations[languageCode] = {};
        }

        const translatedLines = translatedText.split("\n");
        const numberOfLines = Math.min(
          originalLines.length,
          translatedLines.length
        );

        for (let index = 0; index < numberOfLines; index++) {
          const originalLine = originalLines[index].trim();

          // Skip empty lines
          if (originalLine !== "") {
            const translatedLine = translatedLines[index];
            translations[languageCode][originalLine] = translatedLine;
          }
        }

        console.log(
          `Translations for ${languageCode}:`,
          translations[languageCode]
        );
      }

      setStatus("");

      setResultMap(translations);
    } catch (error) {
      console.error(error);
      setError("An error occurred during translation. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleTranslateButton = async () => {
    await translateText();
  
    // Scroll to the resultRef element smoothly
    resultRef.current.scrollIntoView({ behavior: 'smooth' });
  
    // Focus on the element with id="resultTextfield" smoothly
    // const resultInputElement = document.getElementById('resultTextfield');
    // if (resultInputElement) {
    //   resultInputElement.focus({ behavior: 'smooth' });
    // }
  };
  

  useEffect(() => {
    let isMounted = true;
  
    const fetchLanguages = async () => {
      try {
        const response = await axios.get("https://script.google.com/macros/s/AKfycbw8JYC_GXfZ4Sm5PNnvDBtpbM0Q_2nPOrl1Z3SCXGtciYK2YfimIgVxN15HB4KIaTqzhQ/exec?function=getLanguages");
        console.log('response' + response.data)
        if (isMounted) {
          
          setLanguagesList(response.data);
        }
      } catch (error) {
        console.error(error);
      }
    };
  
    fetchLanguages();
  
    return () => {
      isMounted = false;
    };
  }, []); // Pass an empty array as the second argument
  
  useEffect(() => {
    let isMounted = true;

    const translateTextWrapper = async () => {
      await translateText();
    };

    translateTextWrapper();

    return () => {
      isMounted = false;
    };
  }, []);

  return (
    <div className="p-4">
      <div className="flex flex-col md:flex-row">
        {/* input section */}
        <div className="md:w-1/2 p-2">
          <Tooltip
            title={"Type in your text to start translating and localizing"}
          >
            <h1 className="my-4 text-lg font-semibold">Input</h1>
          </Tooltip>
          <p>...select Input Language</p>
          <FormControl fullWidth>
            <Select
              placeholder="Input Language"
              labelId="input-language-label"
              id="input-language"
              value={inputLanguageKey}
              onChange={(e) => setInputLanguageKey(e.target.value)}
            >
              {languageList.map((language) => (
                <MenuItem key={language.code} value={language.code}>
                  {language.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <TextareaAutosize
            className="p-3 w-full my-4 border-2 border-customGray"
            minRows={10}
            value={inputText}
            placeholder={
              "Type text to Translate...\n" +
              "e.g.\n" +
              "Send Button\n" +
              "Login Button\n" +
              "Sending message"
            }
            onChange={(e) => setInputText(e.target.value)}
          />
                <p>...translate into:</p>

          {/* selected languages */}
          <LanguagePicker
            languageList={languageList}
            selectedLanguages={selectedLanguages}
            setSelectedLanguages={setSelectedLanguages}
          ></LanguagePicker>
          <p className="py-2">
            {selectedLanguages.length} <span>selected</span>
          </p>
          {error && (
            <div className="text-red-500 mt-2 text-center py-2">
              Error: {error}. Please try again.
            </div>
          )}
          <div className="text-center md:text-right lg:text-right">
            <Button
              color="primary"
              variant="contained"
              size="medium"
              onClick={handleTranslateButton}
            >
              {loading ? (
                <div className="inline-flex px-2 py-1 ">
                  <span className="pr-4">Translating </span>
                  <div>
                    <CircularProgress size={24} color="inherit" />
                  </div>
                </div>
              ) : (
                <div className="px-2 py-1">
                  Translate
                  <TranslateIcon />
                </div>
              )}
            </Button>
            <p>{status}</p>
          </div>
        </div>
        <div class="border-customGrey border-right sm:border-bottom p-4">
          {/* <!-- Your content goes here --> */}
          <span></span>
        </div>

        {/* output section */}
        <div className="md:w-1/2" ref={resultRef} id="resultRef">
          <h1 className="my-4 text-lg font-semibold">Output</h1>
          <div className="flex-row flex items-center justify-start">
            <ClipBoardComponent
              textToCopy={JSON.stringify(resultMap, null, 2) || ""}
            />
            <div>
              <DownloadButton
                textContent={JSON.stringify(resultMap, null, 2) || ""}
                fileName={"translations"}
              />
            </div>
          </div>
          <TextareaAutosize
          // ref={resultRef}
          id="resultTextfield"
            style={{
              height: "400px",
              width: "100%",
            }}
            className="p-3 mb-3 border-2 border-customGray"
            minRows={30}
            value={JSON.stringify(resultMap, null, 2) || ""}
            placeholder="Your translation result.."
            readOnly
          />
        </div>
      </div>
    </div>
  );
}
